/**
 * @zendir/ui - Responsive Breakpoint System
 *
 * Breakpoints aligned with Astro UX Design System responsive guidelines.
 * Supports mission control displays (4K) down to mobile field devices.
 *
 * Breakpoints:
 * - sm:  640px  — Mobile landscape / small tablets
 * - md:  768px  — Tablets
 * - lg:  1024px — Laptops / desktop
 * - xl:  1280px — Large monitors
 * - xxl: 1920px — Mission control displays (HD)
 *
 * @example
 * ```tsx
 * const { isMobile, isTablet, isDesktop, breakpoint, width } = useBreakpoint();
 * ```
 */

import { useState, useEffect, useCallback, useMemo } from "react";

export type Breakpoint = "base" | "sm" | "md" | "lg" | "xl" | "xxl";

/** Responsive value: single value or [base, sm?, md?, lg?, xl?, xxl?] */
export type ResponsiveValue<T> = T | [T, T?, T?, T?, T?, T?];

export const BREAKPOINTS = {
  base: 0,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  xxl: 1920,
} as const;

const BREAKPOINT_ORDER: Breakpoint[] = ["base", "sm", "md", "lg", "xl", "xxl"];

function getBreakpoint(width: number): Breakpoint {
  if (width >= BREAKPOINTS.xxl) return "xxl";
  if (width >= BREAKPOINTS.xl) return "xl";
  if (width >= BREAKPOINTS.lg) return "lg";
  if (width >= BREAKPOINTS.md) return "md";
  if (width >= BREAKPOINTS.sm) return "sm";
  return "base";
}

export interface UseBreakpointReturn {
  /** Current breakpoint name */
  breakpoint: Breakpoint;
  /** Current viewport width in px */
  width: number;
  /** True when viewport < 768px (base, sm) */
  isMobile: boolean;
  /** True when viewport >= 768px and < 1024px */
  isTablet: boolean;
  /** True when viewport >= 1024px */
  isDesktop: boolean;
  /** True when viewport >= 1920px (mission control) */
  isWidescreen: boolean;
  /** Check if viewport is at or above a given breakpoint */
  isAbove: (bp: Breakpoint) => boolean;
  /** Check if viewport is below a given breakpoint */
  isBelow: (bp: Breakpoint) => boolean;
}

/**
 * Hook for responsive breakpoint detection.
 *
 * Uses matchMedia for efficient, debounce-free breakpoint tracking.
 * Respects SSR by defaulting to 'lg' (desktop).
 *
 * @example
 * ```tsx
 * const { isMobile, isDesktop, breakpoint } = useBreakpoint();
 *
 * return (
 *   <Grid columns={isMobile ? 1 : 3}>
 *     ...
 *   </Grid>
 * );
 * ```
 */
export function useBreakpoint(): UseBreakpointReturn {
  const [width, setWidth] = useState(() => {
    if (typeof window !== "undefined") return window.innerWidth;
    return 1024; // SSR default: desktop
  });

  useEffect(() => {
    if (typeof window === "undefined") return;

    let rafId: number;
    const handleResize = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        setWidth(window.innerWidth);
      });
    };

    window.addEventListener("resize", handleResize, { passive: true });
    return () => {
      window.removeEventListener("resize", handleResize);
      cancelAnimationFrame(rafId);
    };
  }, []);

  const breakpoint = useMemo(() => getBreakpoint(width), [width]);

  const isAbove = useCallback(
    (bp: Breakpoint) => {
      return width >= BREAKPOINTS[bp];
    },
    [width],
  );

  const isBelow = useCallback(
    (bp: Breakpoint) => {
      return width < BREAKPOINTS[bp];
    },
    [width],
  );

  return useMemo(
    () => ({
      breakpoint,
      width,
      isMobile: width < BREAKPOINTS.md,
      isTablet: width >= BREAKPOINTS.md && width < BREAKPOINTS.lg,
      isDesktop: width >= BREAKPOINTS.lg,
      isWidescreen: width >= BREAKPOINTS.xxl,
      isAbove,
      isBelow,
    }),
    [breakpoint, width, isAbove, isBelow],
  );
}

/**
 * Resolve a responsive value based on current breakpoint.
 *
 * Given `[1, 2, 3]` at breakpoint 'lg', returns 3.
 * Falls back to the last defined value for higher breakpoints.
 *
 * @example
 * resolveResponsiveValue([1, 2, 3], 'lg') // → 3
 * resolveResponsiveValue([1, 2], 'xl')     // → 2 (falls back to last defined)
 * resolveResponsiveValue(5, 'md')          // → 5 (non-array passthrough)
 */
export function resolveResponsiveValue<T>(
  value: ResponsiveValue<T>,
  breakpoint: Breakpoint,
): T {
  if (!Array.isArray(value)) return value;

  const index = BREAKPOINT_ORDER.indexOf(breakpoint);

  // Walk backwards from current breakpoint to find a defined value
  for (let i = Math.min(index, value.length - 1); i >= 0; i--) {
    if (value[i] !== undefined && value[i] !== null) return value[i] as T;
  }

  // Fallback: find first defined value in the responsive array.
  for (let i = 0; i < value.length; i++) {
    if (value[i] !== undefined && value[i] !== null) return value[i] as T;
  }

  return value[0] as T;
}
